import classNames from "classnames";
import React, { FC, useEffect, useMemo, useState } from "react";
import { LuChevronRight, LuSquare } from "react-icons/lu";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { LottieLoading } from "../../../../components/graphics/LottieLoading";
import { useAppSelector } from "../../../../redux/redux";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  getSequenceErrorMessage,
  useGetSequenceOptionsQuery,
  useGetSequenceTemplatesQuery,
  useGetWorkSequencesQuery,
  useStartSequenceMutation,
  useStopSequenceMutation,
  useUpdateSequenceMutation,
} from "../../../../slices/EmailSequencesSlice";
import { useGetEmailTemplatesQuery } from "../../../../slices/EmailTemplatesSlice";
import { useGetUsersQuery } from "../../../../slices/UserSlice";
import { useGetWorkQuery } from "../../../../slices/WorkSlice";
import type { AiSequenceState } from "../../../ai/ask/actionProposal.utils";
import { EmailSequence, SequenceForm } from "./emailSequence.types";
import {
  buildSequencePayload,
  describeSequence,
  formatSequenceDate,
  formFromSequence,
  getDefaultForm,
  getSequenceProgress,
} from "./emailSequence.utils";
import { SequenceModal } from "./SequenceModal";

const STATUS_BADGE: Record<string, string> = {
  Active: "bg-[#E7F7EE] text-[#178A4C]",
  Replied: "bg-[#F0EDFF] text-[#5B3FE6]",
  Stopped: "bg-[#F2F3F6] text-[#6B7185]",
  Completed: "bg-[#EEF4FF] text-[#2F5BD3]",
};

export const useSequenceUsers = (orgId: string) => {
  const { data: users = [] } = useGetUsersQuery(orgId, { skip: !orgId });
  return useMemo(
    () =>
      users.map((user) => ({
        id: user.id,
        name: `${user.userProfile?.firstName || ""} ${
          user.userProfile?.lastName || ""
        }`.trim(),
        color: user.userProfile?.color,
      })),
    [users],
  );
};

export const SequenceCard: FC<{
  sequence: EmailSequence;
  templateNames: Record<string, string>;
  onOpen: () => void;
  onStop: () => void;
}> = ({ sequence, templateNames, onOpen, onStop }) => {
  const progress = getSequenceProgress(sequence);
  const isActive = sequence.status === "Active";
  const hasError = sequence.steps.some((step) => !step.sentAt && step.error);

  return (
    <div
      role={"button"}
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter") onOpen();
      }}
      className={classNames(
        "flex cursor-pointer items-center gap-5 rounded-xl border border-[#E4E7EC] bg-white py-4 pl-6 pr-5 transition-shadow hover:shadow-[0_4px_14px_rgba(20,22,34,0.06)]",
        isActive
          ? "border-l-[3px] border-l-[#6B4EFF]"
          : "border-l-[3px] border-l-[#C9CFD9]",
      )}>
      <div className={"min-w-0 flex-1"}>
        <div className={"flex flex-wrap items-center gap-2.5"}>
          <span className={"text-[15px] font-bold text-[#1F2433]"}>
            {sequence.name}
          </span>
          <span
            className={classNames(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase",
              STATUS_BADGE[sequence.status] || STATUS_BADGE.Stopped,
            )}>
            {isActive && (
              <span className={"h-1.5 w-1.5 rounded-full bg-[#22A55B]"} />
            )}
            {sequence.status}
          </span>
        </div>
        <div className={"mt-1 text-[12.5px] text-[#8A90A2]"}>
          Started {formatSequenceDate(sequence.createdAt)}
          {sequence.fromEmail ? ` · from ${sequence.fromEmail}` : ""}
        </div>
        <p className={"mt-2 text-[13.5px] leading-[1.55] text-[#3A4256]"}>
          {describeSequence(sequence, templateNames).map((part, index) =>
            part.strong ? (
              <strong key={index} className={"font-semibold text-[#1F2433]"}>
                {part.text}
              </strong>
            ) : (
              <React.Fragment key={index}>{part.text}</React.Fragment>
            ),
          )}
        </p>
        <div className={"mt-2.5 flex items-center gap-1.5"}>
          {sequence.steps.map((step) => (
            <span
              key={step.id || step.order}
              title={
                step.sentAt
                  ? `Email ${step.order} sent ${formatSequenceDate(
                      step.sentAt,
                    )}`
                  : `Email ${step.order} not sent yet`
              }
              className={classNames(
                "h-2 w-2 rounded-full",
                step.sentAt ? "bg-[#22A55B]" : "bg-[#DCE0E7]",
              )}
            />
          ))}
          <span className={"ml-1.5 text-[12px] text-[#8A90A2]"}>
            {progress.label}
          </span>
        </div>
        {hasError && isActive && (
          <div className={"mt-2 text-[12px] text-[#B42318]"}>
            The last email could not be sent. It will be retried automatically.
            Check that {sequence.fromEmail} is still connected.
          </div>
        )}
      </div>
      {isActive ? (
        <button
          type={"button"}
          onClick={(event) => {
            event.stopPropagation();
            onStop();
          }}
          className={
            "inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#F5C2C2] bg-[#FFF5F5] px-3 text-[13px] font-bold text-[#D92D20] hover:bg-[#FDECEC]"
          }>
          <LuSquare size={9} fill={"currentColor"} /> Stop
        </button>
      ) : (
        <span className={"text-[12.5px] text-[#8A90A2]"}>
          {sequence.status === "Replied" ? "Replied" : sequence.status}
        </span>
      )}
      <LuChevronRight size={16} className={"text-[#B5BAC6]"} />
    </div>
  );
};

export const WorkSequences: FC = () => {
  const { workId = "" } = useParams();
  const { user } = useAppSelector((state) => state.appReducer);
  const orgId = getLinkedFirm()?.orgId || "";

  const { data: work } = useGetWorkQuery({ workId });
  const { data: sequences = [], isLoading } = useGetWorkSequencesQuery(
    { workId },
    { skip: !workId },
  );
  const { data: options, isFetching: isLoadingOptions } =
    useGetSequenceOptionsQuery({ orgId, workId }, { skip: !orgId || !workId });
  const { data: emailTemplates = [] } = useGetEmailTemplatesQuery(
    { orgId },
    { skip: !orgId },
  );
  const { data: sequenceTemplates = [] } = useGetSequenceTemplatesQuery(
    { orgId },
    { skip: !orgId },
  );
  const users = useSequenceUsers(orgId);

  const [startSequence, { isLoading: isStarting }] = useStartSequenceMutation();
  const [updateSequence, { isLoading: isUpdating }] =
    useUpdateSequenceMutation();
  const [stopSequence, { isLoading: isStopping }] = useStopSequenceMutation();

  // Ask ProCharted "start a sequence on this work" opens New Sequence
  // pre-filled (router state, used once); nothing is sent until Start.
  const location = useLocation();
  const navigate = useNavigate();
  const aiSequence = (location.state as AiSequenceState | null)?.aiSequence;
  const [modal, setModal] = useState<
    | { mode: "new"; initial?: SequenceForm }
    | { mode: "edit"; sequence: EmailSequence }
    | null
  >(() => (aiSequence ? { mode: "new", initial: aiSequence } : null));
  useEffect(() => {
    if (aiSequence)
      navigate(location.pathname + location.search, {
        replace: true,
        state: null,
      });
  }, [aiSequence, navigate, location.pathname, location.search]);
  const [stopTarget, setStopTarget] = useState<EmailSequence | null>(null);
  const [error, setError] = useState<string | null>(null);

  const templateNames = useMemo(
    () =>
      Object.fromEntries(
        emailTemplates.map((template) => [template.id, template.name]),
      ),
    [emailTemplates],
  );

  const workName = (work as { name?: string } | undefined)?.name || "";

  const handleSubmit = async (form: SequenceForm) => {
    if (!modal) return;
    setError(null);
    const payload = buildSequencePayload(form, {
      id: modal.mode === "edit" ? modal.sequence.id : undefined,
      orgId,
      workId,
      userId: user?.id || "",
    });
    try {
      if (modal.mode === "edit") {
        await updateSequence(payload).unwrap();
        toast.success("Sequence updated");
      } else {
        await startSequence(payload).unwrap();
        toast.success("Sequence started");
      }
      setModal(null);
    } catch (err) {
      setError(
        getSequenceErrorMessage(err, "Couldn't save the sequence. Try again."),
      );
    }
  };

  const handleStop = async () => {
    if (!stopTarget) return;
    try {
      await stopSequence({
        id: stopTarget.id,
        userId: user?.id || "",
      }).unwrap();
      toast.success("Sequence stopped");
      setStopTarget(null);
      setModal(null);
    } catch (err) {
      toast.error(getSequenceErrorMessage(err, "Couldn't stop the sequence."));
    }
  };

  return (
    <div className={"font-Manrope"}>
      <div className={"mb-4 flex items-center justify-between"}>
        <h2 className={"m-0 text-[18px] font-bold text-[#1F2433]"}>
          Sequences
        </h2>
        <button
          type={"button"}
          onClick={() => {
            setError(null);
            setModal({ mode: "new" });
          }}
          className={
            "h-9 rounded-lg bg-[#6B4EFF] px-4 text-[13.5px] font-bold text-white shadow-[0_6px_16px_rgba(107,78,255,0.25)] hover:bg-[#5A3EEB]"
          }>
          + New Sequence
        </button>
      </div>

      {isLoading ? (
        <LottieLoading />
      ) : sequences.length ? (
        <div className={"grid gap-3"}>
          {sequences.map((sequence) => (
            <SequenceCard
              key={sequence.id}
              sequence={sequence}
              templateNames={templateNames}
              onOpen={() => {
                setError(null);
                if (sequence.status === "Active")
                  setModal({ mode: "edit", sequence });
              }}
              onStop={() => setStopTarget(sequence)}
            />
          ))}
        </div>
      ) : (
        <div
          className={
            "rounded-xl border border-dashed border-[#DCE0E7] bg-white px-6 py-10 text-center"
          }>
          <div className={"text-[15px] font-bold text-[#1F2433]"}>
            No email sequences yet
          </div>
          <p
            className={"mx-auto mt-1 max-w-[420px] text-[13px] text-[#8A90A2]"}>
            Send the client a series of emails, for example a document request
            and reminders. It stops by itself when the client replies.
          </p>
        </div>
      )}

      {modal && (
        <SequenceModal
          key={modal.mode === "edit" ? modal.sequence.id : "new"}
          mode={modal.mode}
          initialForm={
            modal.mode === "edit"
              ? formFromSequence(modal.sequence)
              : modal.initial ??
                getDefaultForm({
                  name: workName ? `Email sequence – ${workName}` : "",
                  fromUserId:
                    options?.mailboxes.find((box) => box.userId === user?.id)
                      ?.userId || "",
                  toEmail: "",
                })
          }
          options={options}
          currentFromEmail={
            modal.mode === "edit" ? modal.sequence.fromEmail : null
          }
          isLoadingOptions={isLoadingOptions}
          emailTemplates={emailTemplates}
          sequenceTemplates={sequenceTemplates}
          users={users}
          workName={workName}
          isSaving={isStarting || isUpdating}
          error={error}
          onClose={() => setModal(null)}
          onSubmit={handleSubmit}
          onStop={
            modal.mode === "edit"
              ? () => setStopTarget(modal.sequence)
              : undefined
          }
        />
      )}

      {stopTarget && (
        <div
          className={
            "fixed inset-0 z-[90] flex items-center justify-center bg-[rgba(20,22,34,0.45)] p-4 font-Manrope"
          }>
          <div
            role={"alertdialog"}
            aria-label={"Stop sequence"}
            className={
              "w-[400px] max-w-full rounded-2xl bg-white p-6 shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
            }>
            <span
              className={
                "flex h-10 w-10 items-center justify-center rounded-xl bg-[#FEE4E2] text-[#D92D20]"
              }>
              <LuSquare size={14} />
            </span>
            <h4
              className={
                "mb-1.5 mt-4 text-[17px] font-extrabold text-[#1F2433]"
              }>
              Stop this email sequence?
            </h4>
            <p className={"text-[13.5px] leading-[1.5] text-[#5B6678]"}>
              The remaining emails in &quot;{stopTarget.name}&quot; will not be
              sent. This cannot be undone, but you can start a new sequence
              anytime.
            </p>
            <div className={"mt-5 grid grid-cols-2 gap-2.5"}>
              <button
                type={"button"}
                onClick={() => setStopTarget(null)}
                className={
                  "h-10 rounded-lg border border-[#DCE2EA] text-[14px] font-semibold text-[#1F2433]"
                }>
                Cancel
              </button>
              <button
                type={"button"}
                disabled={isStopping}
                onClick={() => void handleStop()}
                className={
                  "h-10 rounded-lg bg-[#D92D20] text-[14px] font-bold text-white hover:bg-[#B42318] disabled:opacity-50"
                }>
                Stop Sequence
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
